import { useEffect, useRef, useState, type CSSProperties } from "react";
import { playMagicSfx } from "../lib/magicSound";
/**
 * 🧊 **아이 화면과 같은 3D 무대** — 책 뷰어가 쓰는 그 컴포넌트를 그대로 가져온다(소리 재생기와 같은 방식).
 * 관리자에 따로 짜면 크기 재기·조명·각도 셈이 한쪽만 고쳐진다. 엔진(A-Frame)은 3D 가 있는 새 화면이 열릴 때만 늦게 싣는다.
 */
import { BookModelStage, bookModelTurnNow } from "../../../web/src/book/BookModelStage";
import type { MagicModelReq } from "../../../web/src/ar/magicModel";
import {
  AR_FADE_MS,
  applyAction,
  goBack,
  hitHotspot,
  initialLayerState,
  layerBox,
  fullLayersOn,
  layerShown,
  layerEnter,
  layerMotionVars,
  layerPlayKey,
  stripFrames,
  stripStyle,
  stripFrameStyle,
  spinTarget,
  spinDrag,
  spinFrame,
  spinMode,
  spinSet,
  spinView,
  spinTilt,
  spinPinch,
  turnFromTransform,
  fullCredits,
  SPIN_TAP_SLACK,
  SPIN_AUTO_FPS,
  zoomedLayer,
  dismissSaid,
  MAGIC_ZOOM_BOX,
  hintSpots,
  type ArInteract,
  type ArState,
  layerStripDeferred,
  modelModeLabel,
} from "@greg-chick/shared";

/**
 * 👀 **마법 미리보기** — 아이가 이 쪽에서 누르면 무엇이 되는지(docs/build/35, FR-AR-09).
 *
 * ## 왜 필요한가
 * 운영자는 층·누르는 자리·손잡이를 만들지만 **결과를 볼 길이 없다.** 저장하고 게시하고 아이
 * 화면을 열어 책을 넘겨야 확인이 된다 — 그 사이 하나라도 어긋나면 「분명 만들었는데 안 나온다」가
 * 되고, 어디가 틀렸는지 알 수가 없다.
 *
 * ## 🔴 판정을 다시 짜지 않는다
 * 무엇이 보이나(`layerOpacity`) · 누르면 무엇이 되나(`applyAction`) · 얼마나 가까우면 눌리나
 * (`hitHotspot`) · 처음 상태(`initialLayerState`) · 돌아가기(`goBack`) — 전부 **아이 화면
 * (`apps/web/src/book/PageMagic.tsx`)과 같은 shared 함수**다. 여기서 다시 짜면 그럴듯하게
 * 동작하면서 조용히 갈린다(`previewParity.test.ts` 가 그래서 있다).
 *
 * 그리는 건 **모양이 아니라 내용**이다. 운영자가 알아야 하는 건 「누르면 무엇이 보이는가」이고,
 * 그건 판정이 정한다. 색·간격을 아이 화면과 픽셀까지 맞추려 들지 않는다.
 */
export function PageMagicPreview({
  def,
  layers,
  models,
}: {
  def: ArInteract;
  layers: Record<string, { url: string; fps?: number; once?: boolean }>;
  /** 🧊 층 id → 3D 파일(.glb) 여는 주소. 돌려 보는 층에 있으면 띠 대신 3D 를 세운다(아이 화면과 같다). */
  models?: Record<string, string>;
}) {
  const [state, setState] = useState<ArState>(() => initialLayerState(def));
  /**
   * 🎞 **띠를 아이 화면처럼 돌린다** — 몇 장인지는 그림 크기로(shared `stripFrames`), 넘기는 법은 shared `stripStyle`.
   * 🔴 전에는 띠를 그냥 `<img>` 한 장으로 그렸다 — 12장짜리 꼬투리가 **가느다란 띠 한 줄**로 납작하게
   *    보였다(2026-09-11). 운영자는 게시 전에 이 화면으로 확인한다.
   */
  const [frames, setFrames] = useState<Record<string, number>>({});
  /**
   * 👆 **누름 반응** — 아이 화면과 같은 반응. 누른 자리에 물결 한 번, 눌린 자리가 붙은 층이 살짝 움찔.
   * 자리(x·y)는 손가락이 닿은 곳(0~1), 층은 shared `hitHotspot` 이 고른 자리의 층이다.
   * 🔴 `animationend` 로 지우지 않는다 — 「움직임을 줄여 달라」면 애니가 없어 그 신호가 영영 안 온다.
   */
  const [tapFx, setTapFx] = useState<{ x: number; y: number; layer: string; n: number } | null>(null);
  useEffect(() => {
    if (!tapFx) return;
    const t = window.setTimeout(() => setTapFx(null), 450);
    return () => window.clearTimeout(t);
  }, [tapFx]);
  /**
   * 👆 **아이가 보는 안내를 운영자도 본다** — 자리를 옮기면 동그라미도 따라 옮겨진다.
   * 기다림은 짧게(운영자는 이미 답을 안다). 판정(`hintSpots`)은 아이 화면과 같은 곳.
   */
  const [hintOn, setHintOn] = useState(false);
  const [tapped, setTapped] = useState(false);
  useEffect(() => {
    if (tapped) return;
    const t = window.setTimeout(() => setHintOn(true), 1200);
    return () => window.clearTimeout(t);
  }, [tapped]);
  const said = state.said;
  /**
   * 🖼️ **새 화면**(`full` 층이 보이는 동안) — 아이 화면과 **같은 판정**(`fullLayersOn`).
   *
   * 🔴 이걸 빠뜨렸었다(2026-09-09). 운영자가 「🖼️ 새 화면으로 열기」를 켜도 미리보기는 예전처럼
   * 쪽 위에 겹쳐 보여 줬다 — 화면은 그럴듯한데 **아이가 보는 것과 다른** 상태다. 이 저장소가
   * 반나절 버린 바로 그 갈림이라 `previewParity` 가 이제 셋 다 강제한다.
   */
  const fullOn = fullLayersOn(def, state);
  const inFull = fullOn.length > 0;
  const fullIds = new Set(fullOn.map((l) => l.id));
  /** 🔍 아이 화면과 같은 판정 — 유물 하나를 크게 본다. */
  const zoom = zoomedLayer(def, state);
  /**
   * 🔄 **끌면 돌아간다** — 새 화면의 `spin` 층(3D 를 10~15도씩 찍은 띠). 어느 층이 도는지(`spinTarget`)·
   * 얼마나 도는지(`spinDrag`)·몇 번째 장인지(`spinFrame`)는 shared — 세 화면이 같은 셈이다.
   * 🔴 끌고 난 뒤 오는 click 은 **누름이 아니다** — 무시하지 않으면 끌어 돌린 끝에 자리가 눌린다.
   */
  const dragRef = useRef<{ x: number; y: number; moved: boolean; id: string } | null>(null);
  const swallowClickRef = useRef(false);
  const spinId = spinTarget(def, state);
  /**
   * 🧊 **바퀴 = 두 손가락 벌리기**(아이 화면과 같은 셈) — 운영자가 「키우면 어떻게 보이나」를 볼 수 있게.
   * 🔴 React 의 `onWheel` 은 **수동(passive) 듣기**라 `preventDefault` 가 안 먹는다(2026-09-13 진짜 크롬 걸음에서
   *    「Unable to preventDefault inside passive event listener」). 그러면 키우는 동안 **관리자 화면이 같이 스크롤**된다.
   *    그래서 무대에 직접 `passive: false` 로 붙인다. 값은 ref 로 — 매 렌더마다 떼었다 붙이지 않게.
   */
  const stageRef = useRef<HTMLDivElement>(null);
  const wheelRef = useRef({ spinId, inFull });
  wheelRef.current = { spinId, inFull };
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      const { spinId: id, inFull: full } = wheelRef.current;
      if (!id || !full) return;
      e.preventDefault();
      setState((s) => spinPinch(s, id, e.deltaY < 0 ? 1.1 : 1 / 1.1));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);
  /**
   * 🧊 **띠 대신 진짜 3D** (2026-09-13 · 운영자 「28쪽 수막새 AR 보기가 관리자에 없는데 3D 되는 거 맞아?」).
   * 전에는 미리보기가 띠만 돌려서 운영자는 3D 가 서는지 볼 길이 없었다. 요청 모양·각도·기울기·배율은 아이 화면
   * (`PageMagic`)과 같은 shared 셈이고, 못 불러오면 띠로 돌아가며 **그렇다고 말한다.**
   */
  const [modelFailed, setModelFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    for (const [id, l] of Object.entries(layers)) {
      if (layerStripDeferred(models, id, modelFailed)) continue; // 🧊 3D 가 서면 띠는 안 받는다 — 못 불러오면 그때(shared)
      const img = new Image();
      img.onload = () => {
        if (!alive) return;
        const n = stripFrames(img.naturalWidth, img.naturalHeight);
        setFrames((f) => (f[id] === n ? f : { ...f, [id]: n }));
      };
      img.src = l.url;
    }
    return () => {
      alive = false;
    };
  }, [layers, models, modelFailed]);
  const modelUrl = spinId && !modelFailed ? (models?.[spinId] ?? null) : null;
  const modelReq: MagicModelReq | null =
    modelUrl && spinId
      ? {
          layerId: spinId,
          url: modelUrl,
          turn: state.spin?.[spinId] ?? 0,
          auto: spinMode(def.layers.find((l) => l.id === spinId)!, state, spinId) === "auto",
          frames: frames[spinId] ?? 24,
          tilt: spinView(state, spinId).tilt,
          zoom: spinView(state, spinId).zoom,
        }
      : null;

  return (
    <div className="magicprev">
      <div
        ref={stageRef}
        className={`magicprev__stage${inFull ? " magicprev__stage--full" : ""}${spinId ? " magicprev__stage--spin" : ""}`}
        onPointerDown={(e) => {
          if (!spinId) return;
          e.preventDefault(); // 글자 선택·끌어 옮기기가 손가락을 끊지 않게(아이 화면과 같다)
          dragRef.current = { x: e.clientX, y: e.clientY, moved: false, id: spinId };
          e.currentTarget.setPointerCapture?.(e.pointerId);
          // 🔄 저절로 돌던 층을 잡았다 — 지금 보이던 장에서 이어받는다(아이 화면과 같은 셈)
          //    띠면 CSS 에서, 3D 면 무대의 각도에서(아이 화면과 같은 규칙)
          if (spinMode(def.layers.find((l) => l.id === spinId)!, state, spinId) === "auto") {
            const img = e.currentTarget.querySelector<HTMLElement>(`[data-layer="${spinId}"] img`);
            const n = frames[spinId] ?? 1;
            const turn = modelReq
              ? bookModelTurnNow(e.currentTarget)
              : img
                ? turnFromTransform(getComputedStyle(img).transform, img.clientWidth / n, n)
                : null;
            if (turn !== null) setState((s) => spinSet(s, spinId, turn));
          }
        }}
        onPointerMove={(e) => {
          const d = dragRef.current;
          const box = e.currentTarget.getBoundingClientRect();
          if (!d || box.width === 0) return;
          const dx = (e.clientX - d.x) / box.width;
          const dy = box.height ? (e.clientY - d.y) / box.height : 0;
          if (!d.moved && Math.abs(dx) < SPIN_TAP_SLACK && Math.abs(dy) < SPIN_TAP_SLACK) return;
          d.moved = true;
          d.x = e.clientX;
          d.y = e.clientY;
          setTapped(true);
          setHintOn(false);
          // 좌우 = 돌기 · 위아래 = 기울이기 — 아이 화면과 같은 셈(shared). 3D 면 기울기까지 보인다(띠는 배율만)
          setState((s) => spinTilt(spinDrag(s, d.id, dx), d.id, dy));
        }}
        onPointerUp={() => {
          if (dragRef.current?.moved) swallowClickRef.current = true;
          dragRef.current = null;
        }}
        onPointerCancel={() => {
          dragRef.current = null;
        }}
        onClick={(e) => {
          if (swallowClickRef.current) {
            swallowClickRef.current = false;
            return;
          }
          const box = e.currentTarget.getBoundingClientRect();
          if (box.width === 0) return;
          const rx = (e.clientX - box.left) / box.width;
          const ry = (e.clientY - box.top) / box.height;
          const hit = hitHotspot(def, state, rx, ry);
          if (hit) setTapFx((f) => ({ x: rx, y: ry, layer: hit.layer, n: (f?.n ?? 0) + 1 })); // 👆 아이 화면과 같은 반응
          // 새 화면에선 그 화면의 층에 붙은 자리만 — 쪽 위 자리가 뒤에서 눌리면 안 된다
          if (hit && (!inFull || fullIds.has(hit.layer))) {
            // 🔊 운영자도 **아이가 듣는 그 소리**를 들어야 고를 수 있다(같은 재생기)
            playMagicSfx(hit.action.sfx);
            setTapped(true);
            setHintOn(false);
            setState((s) => applyAction(s, hit.action, hit.id));
            return;
          }
          setState(dismissSaid); // 🔍 자리가 아닌 곳 — 크게 본 것을 제자리로
        }}
      >
        {inFull ? <div className="magicprev__full" aria-hidden="true" /> : null}
        {/* 🧊 진짜 3D — 아이 화면과 같은 무대. 못 실으면 띠로 돌아간다(modelFailed) */}
        {inFull && modelReq ? <BookModelStage req={modelReq} onFailed={() => setModelFailed(true)} /> : null}
        {inFull && spinId && models?.[spinId] ? (
          <div className={`magicprev__mode${modelReq ? "" : " magicprev__mode--fallback"}`} role="status">
            {modelModeLabel(!!modelReq, !!layers[spinId])}
          </div>
        ) : null}
        {tapFx ? (
          <span
            key={`ripple:${tapFx.n}`}
            className="magicprev__ripple"
            aria-hidden="true"
            data-ripple
            style={{ left: `${tapFx.x * 100}%`, top: `${tapFx.y * 100}%` }}
          />
        ) : null}
        {hintOn
          ? hintSpots(def, state).map((h) => (
              <span
                key={`hint:${h.id}`}
                className="magicprev__hint"
                aria-hidden="true"
                style={{ left: `${(h.rect.x + h.rect.w / 2) * 100}%`, top: `${(h.rect.y + h.rect.h / 2) * 100}%` }}
              />
            ))
          : null}
        {def.layers.map((l) => {
          const img = layers[l.id];
          if (!img) return null;
          if (modelReq?.layerId === l.id) return null; // 🧊 3D 로 세운 층은 띠를 안 그린다(둘이 겹친다 — 아이 화면과 같다)
          if (layerStripDeferred(models, l.id, modelFailed)) return null; // 🧊 3D 가 서면 띠를 안 받는다(아이 화면과 같은 규칙)
          const shown = layerShown(def, state, l.id); // 👁 아이 화면과 같은 마지막 판정
          const enter = layerEnter(l, shown); // ✨ 아이 화면과 같은 등장 판정(shared)
          const n = frames[l.id] ?? 1;
          /*
           * 🔴 **층(상자) 안에 그림** — 아이 화면과 같은 두 겹. 등장 연출은 상자에, 움직임·띠는 그림에.
           *    한 겹에 둘 다 붙이면 등장 애니가 움직임을 덮어 **보리밭이 미리보기에서만 안 흔들렸다.**
           */
          return (
            <div
              key={l.id}
              className={`magicprev__layer${said?.focus === l.id && !zoom ? " magicprev__layer--focus" : ""}${zoom === l.id ? " magicprev__layer--zoom" : ""}${enter ? ` magicprev__layer--enter-${enter.enter}` : ""}${tapFx?.layer === l.id && said?.focus !== l.id && zoom !== l.id ? " magicprev__layer--tapped" : ""}`}
              data-layer={l.id}
              aria-hidden="true"
              style={{
                ...layerBox(zoom === l.id ? { ...l, rect: MAGIC_ZOOM_BOX } : l),
                ...(spinId === l.id ? { transform: `scale(${spinView(state, l.id).zoom})` } : {}), // 🧊 두 손가락 배율(아이 화면과 같은 셈) // 아이 화면과 같은 자리(shared 한 곳)
                opacity: shown,
                transition: `opacity ${AR_FADE_MS}ms, left ${AR_FADE_MS}ms, top ${AR_FADE_MS}ms, width ${AR_FADE_MS}ms, height ${AR_FADE_MS}ms`,
                ...(enter ? { transitionDelay: `${enter.delayMs}ms`, ["--enter-delay" as string]: `${enter.delayMs}ms` } : {}),
                ...layerMotionVars(l), // 🌾 엇박 — 줄마다 조금씩 앞선 자세로(shared 한 곳)
              }}
            >
              <img
                key={img.once ? layerPlayKey(l.id, shown, state) : l.id}
                className={`magicprev__img${n > 1 ? " magicprev__img--strip" : ""}${l.motion ? ` magicprev__img--${l.motion}` : ""}`}
                src={img.url}
                alt=""
                draggable={false}
                style={
                  n > 1
                    ? ((spinMode(l, state, l.id) === "drag"
                        ? stripFrameStyle(n, spinFrame(state, l.id, n))
                        : spinMode(l, state, l.id) === "auto"
                          ? stripStyle(n, SPIN_AUTO_FPS, false)
                          : stripStyle(n, img.fps, img.once)) as CSSProperties)
                    : undefined
                }
              />
            </div>
          );
        })}
        {inFull && fullCredits(def, state).length > 0 ? (
          <div className="magicprev__credit">{fullCredits(def, state).join(" · ")}</div>
        ) : null}
        {inFull && spinId && state.spin?.[spinId] === undefined ? (
          <div className="magicprev__spinhint" aria-hidden="true">{modelReq ? "◀ 끌어 돌리고 · 바퀴로 키우고 · 위아래로 기울여 보세요 ▶️" : "◀ 끌어서 돌려 보세요 ▶️"}</div>
        ) : null}
      </div>

      <div className="magicprev__foot">
        {def.slider ? (
        <label className="magicprev__slider">
          <span aria-hidden="true">{def.slider.iconA ?? "◐"}</span>
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(state.mix * 100)}
            aria-label={`미리보기 — ${def.slider.label_ko}`}
            onChange={(e) => {
              const mix = Number(e.target.value) / 100;
              setState((s) => ({ ...s, mix }));
            }}
          />
          <span aria-hidden="true">{def.slider.iconB ?? "◑"}</span>
        </label>
      ) : null}

      {said ? (
        <div className="magicprev__say">
          <b>{said.child_ko}</b>
          {said.guardian_ko ? <span> · 보호자용: {said.guardian_ko}</span> : null}
        </div>
      ) : null}
      {/* 👨‍👩‍👧 보여주기 자리의 보호자 문구 — 아이 화면과 같은 판정(state.hint) */}
      {!said && state.hint ? (
        <div className="magicprev__say" data-guardian-hint>
          <span>보호자용: {state.hint.guardian_ko}</span>
        </div>
      ) : null}

        <button type="button" className="btn btn--sm" onClick={() => setState((s) => goBack(def, s))}>
          ← 처음으로
        </button>
      </div>
    </div>
  );
}
